import { createReducer } from '@reduxjs/toolkit';
import { enqueueSnackbar } from '../notify';
import getApiCaller from 'src/utils/apiClientCaller';
import smjApi from 'src/utils/api/smartjourneyApi';

import { loaderActions } from '../loader';
import { loadRequestModels } from '../requestModelList';
import i18next from 'i18next';

const initialState = {
  currentStep: 0,
  smjListsByModel: [], // not by model finally
  smjModelList: [],
  smjInfos: {},
};

export const enableSMJ = (smj, showAlert) => async (dispatch, getState) => {
  const setLoader = visible => {
    dispatch(loaderActions.setLoader(visible));
  };
  const callApi = getApiCaller(showAlert)(dispatch, getState);

  const asyncFn = async () => {
    setLoader(true);
    const activationResponse = await callApi(smjApi.activateSMJ, [smj.id, !smj.enabled]);

    if (activationResponse.isSuccess) {
      dispatch(
        enqueueSnackbar({
          message: `${smj.name}: ${
            !smj.enabled
              ? i18next.t('smartmessaging.notifications.enabledCampaign')
              : i18next.t('smartmessaging.notifications.disabledCampaign')
          }`,

          options: {
            variant: 'info',
          },
        })
      );
      const upToDateList = [...getState().smartjourney.smjList].map(listSMJ => {
        if (listSMJ.id === smj.id) {
          return { ...smj, enabled: !smj.enabled };
        }
        return listSMJ;
      });

      dispatch({ type: 'RECEIVE_SMJ_LIST', value: upToDateList });
    } else {
      showAlert({
        type: 'warning',
        title: i18next.t('smartmessaging.errorMessages.smartjourney.notActivable.title'),
        msg: i18next.t(
          (activationResponse.error &&
            `smartmessaging.errorMessages.smartjourney.notActivable.${activationResponse.error}`) ||
            'smartmessaging.errorMessages.smartjourney.notActivable.message'
        ),
      });
    }
  };
  await asyncFn().finally(() => {
    setLoader(false);
  });
};

const receiveSMJList = (state, { value }) => ({
  ...state,
  smjListsByModel: value,
});

const receiveSMJInfos = (state, { value }) => ({
  ...state,
  smjInfos: value,
});

const receiveSMJModelList = (state, { value }) => ({
  ...state,
  smjModelList: value,
});

export const loadSMJs = showAlert => async (dispatch, getState) => {
  const apiCaller = getApiCaller(showAlert)(dispatch, getState);
  const smjList = await apiCaller(smjApi.getSMJList);
  let infos = [];
  if (smjList && smjList.length) {
    infos = await apiCaller(smjApi.getSmjInfosById, [smjList.map(s => s.id)]);
  }
  dispatch({ type: 'RECEIVE_SMJ_LIST', value: smjList });
  dispatch({ type: 'RECEIVE_SMJ_INFOS', value: infos });
};

export const createSMJ = ({ cmpList, smj, language }, onSuccess, onFail, showAlert) => async (
  dispatch,
  getState
) => {
  const apiCaller = getApiCaller(showAlert)(dispatch, getState);
  await apiCaller(smjApi.createSMJ, [
    {
      cmpList: cmpList.map(cmp => ({
        ...cmp,
        name: i18next.t(`smartmessaging.campaigns.grouped.campaignModel.name.${cmp.key}`),
      })),
      smj,
      language,
    },
  ]).then(
    async res => {
      if (!res) {
        showAlert({
          type: 'warning',
          title: i18next.t('smartmessaging.smartjourney.createdWithErrors.title'),
          msg: i18next.t('smartmessaging.smartjourney.createdWithErrors.title'),
        });
      }
      dispatch(
        enqueueSnackbar({
          message: `${smj.name}: ${i18next.t('smartmessaging.notifications.smj.createdSuccess')}`,
          options: {
            variant: 'success',
          },
        })
      );
      if (onSuccess) {
        dispatch(loadSMJs(showAlert));
        const newSmj = await apiCaller(smjApi.getCampaignGroup, [res]);
        onSuccess(newSmj);
      }
    },
    () => {
      dispatch(
        enqueueSnackbar({
          message: `${smj.name}: ${i18next.t('smartmessaging.notifications.smj.createFailure')}`,
          options: {
            variant: 'warning',
          },
        })
      );
      if (onFail) onFail();
    }
  );
};

export const initData = showAlert => async (dispatch, getState) => {
  const apiCaller = getApiCaller(showAlert)(dispatch, getState);
  await dispatch(loadRequestModels(showAlert));
  await dispatch(loadSMJs(showAlert));
  const smjModelList = await apiCaller(smjApi.getSMJModelList);
  dispatch({
    type: 'RECEIVE_SMJ_MODEL_LIST',
    value: Object.entries(smjModelList).map(([id, model]) => ({ id, ...model })),
  });
};

export default createReducer(initialState, {
  RECEIVE_SMJ_LIST: receiveSMJList,
  RECEIVE_SMJ_INFOS: receiveSMJInfos,
  RECEIVE_SMJ_MODEL_LIST: receiveSMJModelList,
});
